import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, BellRing, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PushNotificationManager() {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkPermissionAndSubscription();
  }, []);

  const checkPermissionAndSubscription = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    setPermission(Notification.permission);

    if (Notification.permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    }
    
    // Removed auto-popup - alerts option now visible in navbar
  };

  const subscribeToPush = async () => {
    setIsLoading(true);
    try {
      // Request permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        toast.error('Notification permission denied');
        setIsLoading(false);
        return;
      }

      // Get VAPID public key
      const keyRes = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = keyRes.data.publicKey;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        },
        preferences: {
          breaking_news: true,
          daily_digest: false,
          party_alerts: ['all']
        }
      });

      setIsSubscribed(true);
      setShowPrompt(false);
      toast.success('You\'ll receive alerts for breaking political news!');
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: subscription.endpoint,
          keys: {}
        });
      }

      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if notifications not supported
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  // No auto-popup - notification option is in navbar/settings
  return null;
}

// Compact notification toggle button for settings page
export function NotificationToggle() {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    if (!('Notification' in window)) return;
    
    setPermission(Notification.permission);
    
    if (Notification.permission === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const toggleNotifications = async () => {
    setIsLoading(true);
    try {
      if (isSubscribed) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await axios.post(`${API}/push/unsubscribe`, {
            endpoint: subscription.endpoint,
            keys: {}
          });
        }
        setIsSubscribed(false);
        toast.success('Notifications disabled');
      } else {
        // Subscribe
        const result = await Notification.requestPermission();
        setPermission(result);
        
        if (result !== 'granted') {
          toast.error('Permission denied');
          setIsLoading(false);
          return;
        }

        const keyRes = await axios.get(`${API}/push/vapid-public-key`);
        const vapidPublicKey = keyRes.data.publicKey;

        const urlBase64ToUint8Array = (base64String) => {
          const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
          const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          return Uint8Array.from(rawData, c => c.charCodeAt(0));
        };

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        await axios.post(`${API}/push/subscribe`, {
          subscription: {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          },
          preferences: { breaking_news: true }
        });

        setIsSubscribed(true);
        toast.success('Notifications enabled!');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update notifications');
    } finally {
      setIsLoading(false);
    }
  };

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return (
      <div className="flex items-center justify-between p-4 bg-slate-100 rounded-xl">
        <div className="flex items-center gap-3">
          <BellOff className="h-5 w-5 text-slate-400" />
          <span className="text-slate-500">Notifications not supported</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
      <div className="flex items-center gap-3">
        {isSubscribed ? (
          <div className="p-2 bg-green-100 rounded-lg">
            <Bell className="h-5 w-5 text-green-600" />
          </div>
        ) : (
          <div className="p-2 bg-slate-100 rounded-lg">
            <BellOff className="h-5 w-5 text-slate-400" />
          </div>
        )}
        <div>
          <p className="font-medium text-slate-900">Push Notifications</p>
          <p className="text-sm text-slate-500">
            {isSubscribed ? 'Receiving breaking news alerts' : 'Get alerts for breaking news'}
          </p>
        </div>
      </div>
      
      <Button
        onClick={toggleNotifications}
        disabled={isLoading}
        variant={isSubscribed ? "outline" : "default"}
        className={isSubscribed ? "" : "bg-blue-600 hover:bg-blue-700"}
      >
        {isLoading ? (
          'Loading...'
        ) : isSubscribed ? (
          <>
            <Check className="h-4 w-4 mr-2" />
            Enabled
          </>
        ) : (
          'Enable'
        )}
      </Button>
    </div>
  );
}
